import {
  collection,
  doc,
  getDoc,
  orderBy,
  serverTimestamp,
  increment,
  writeBatch,
  updateDoc,
  QueryDocumentSnapshot,
  DocumentData,
} from '@firebase/firestore';
import { db } from './firebase';
import { COLLECTIONS } from './collections';
import { Comment } from '../types';
import type { CreateCommentData } from '../types/api';
import { mapDoc, paginatedQuery } from '@utils/firestore';
import { AppError } from '../utils/errorHandler';
import { CommentSchema } from '../schemas/firestoreSchemas';

export type { CreateCommentData } from '../types/api';

// Opaque pagination cursor for getComments. Components hold and pass this
// back but never inspect it — keeps @firebase/firestore types out of the
// component layer.
export type CommentsCursor = QueryDocumentSnapshot<DocumentData>;

const COMMENTS_PER_PAGE = 20;

export const commentsApi = {
  async createComment(data: CreateCommentData): Promise<Comment> {
    const commentData = {
      authorId: data.authorId,
      authorName: data.authorName,
      text: data.text.trim(),
      createdAt: serverTimestamp(),
    };

    const batch = writeBatch(db);

    const commentRef = doc(collection(db, COLLECTIONS.POSTS, data.postId, 'comments'));
    batch.set(commentRef, commentData);

    batch.update(doc(db, COLLECTIONS.POSTS, data.postId), {
      commentsCount: increment(1),
    });

    await batch.commit();

    const docSnap = await getDoc(commentRef);
    const created = mapDoc(docSnap, CommentSchema);
    if (!created) {
      throw new AppError('Comment was created but could not be re-read.', 'FIRESTORE_ERROR');
    }
    return created;
  },

  async getComments(
    postId: string,
    lastDoc?: CommentsCursor,
    pageSize = COMMENTS_PER_PAGE
  ): Promise<{ comments: Comment[]; lastDoc: CommentsCursor | null }> {
    const result = await paginatedQuery(
      collection(db, COLLECTIONS.POSTS, postId, 'comments'),
      [orderBy('createdAt', 'asc')],
      lastDoc,
      pageSize,
      CommentSchema
    );
    return { comments: result.items, lastDoc: result.lastDoc };
  },

  async deleteComment(postId: string, commentId: string): Promise<void> {
    const batch = writeBatch(db);

    const commentRef = doc(db, COLLECTIONS.POSTS, postId, 'comments', commentId);
    batch.delete(commentRef);

    batch.update(doc(db, COLLECTIONS.POSTS, postId), {
      commentsCount: increment(-1),
    });

    await batch.commit();
  },

  async updateComment(postId: string, commentId: string, text: string): Promise<void> {
    const commentRef = doc(db, COLLECTIONS.POSTS, postId, 'comments', commentId);
    await updateDoc(commentRef, { text: text.trim(), updatedAt: serverTimestamp() });
  },
};
